import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogIn, LogOut, Crown, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/chat", label: "Chat" },
  { href: "/my-recipes", label: "My Recipes" },
  { href: "/how-it-works", label: "How It Works" },
];

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { user, subscription, signOut, isLoading } = useAuth();

  const isSubscribed = subscription.subscribed;

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="font-display text-xl font-semibold">Kitchen Assistant X</span>
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden md:flex md:items-center md:gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={cn(
                "text-sm font-medium transition-colors hover:text-primary",
                location.pathname === link.href
                  ? "text-primary"
                  : "text-muted-foreground"
              )}
            >
              {link.label}
            </Link>
          ))}
          
          {!isLoading && (
            <>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <User className="h-4 w-4" />
                      {isSubscribed && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          Pro
                        </Badge>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium truncate">{user.email}</p>
                      {isSubscribed ? (
                        <p className="text-xs text-primary">Pro Member</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Free Plan</p>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/my-recipes">My Recipes</Link>
                    </DropdownMenuItem>
                    {!isSubscribed && (
                      <DropdownMenuItem asChild>
                        <Link to="/pricing" className="text-primary">
                          <Crown className="h-4 w-4 mr-2" />
                          Upgrade to Pro
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => signOut()}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/auth">
                      <LogIn className="h-4 w-4 mr-1" />
                      Sign In
                    </Link>
                  </Button>
                  <Button size="sm" asChild>
                    <Link to="/chat">Try it Now</Link>
                  </Button>
                </div>
              )}
            </>
          )}
        </nav>

        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile navigation */}
      {isMenuOpen && (
        <nav className="border-t bg-background px-4 py-4 md:hidden">
          <div className="flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  location.pathname === link.href
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {link.label}
              </Link>
            ))}
            
            {!isLoading && (
              <>
                {user ? (
                  <>
                    <div className="border-t pt-3 mt-2">
                      <p className="px-3 text-sm text-muted-foreground truncate">
                        {user.email}
                      </p>
                      {isSubscribed && (
                        <Badge variant="secondary" className="ml-3 mt-1">
                          Pro Member
                        </Badge>
                      )}
                    </div>
                    {!isSubscribed && (
                      <Link
                        to="/pricing"
                        onClick={() => setIsMenuOpen(false)}
                        className="rounded-md px-3 py-2 text-sm font-medium text-primary hover:bg-muted flex items-center gap-2"
                      >
                        <Crown className="h-4 w-4" />
                        Upgrade to Pro
                      </Link>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start"
                      onClick={() => {
                        signOut();
                        setIsMenuOpen(false);
                      }}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild size="sm" variant="outline" className="mt-2">
                      <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                        <LogIn className="h-4 w-4 mr-2" />
                        Sign In
                      </Link>
                    </Button>
                    <Button asChild size="sm" className="mt-2">
                      <Link to="/chat" onClick={() => setIsMenuOpen(false)}>
                        Try it Now
                      </Link>
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
